import Button from 'devextreme-react/button';
import type { ToolbarItem } from 'devextreme/ui/popup';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showToast } from '../notifications/toast';
import { useDiscussionMessageNotifications } from '../notifications/useDiscussionMessageNotifications';
import { PatientTrackingDiscussionChat } from '../patientTracking/discussion/PatientTrackingDiscussionChat';
import { usePatientTrackingDiscussion } from '../patientTracking/discussion/usePatientTrackingDiscussion';
import type { PatientTrackingDiscussionTargetType } from '../patientTracking/discussion/patientTrackingDiscussionTypes';
import {
  readAutoHideRecordDiscussionPanel,
  writeRecordDiscussionPlacement,
  type RecordDiscussionPlacement
} from '../patientTracking/discussion/recordDiscussionPrefs';
import { useRecordDiscussionAutoHide } from '../patientTracking/discussion/useRecordDiscussionAutoHide';
import { useRecordDiscussionPlacement } from '../patientTracking/discussion/useRecordDiscussionPlacement';
import { DiscussionErrorBoundary } from './DiscussionErrorBoundary';
import { GridFormPopup, type GridFormPopupProps } from './GridFormPopup';
import './recordEditDiscussionPopup.css';

export interface RecordEditDiscussionPopupProps extends Omit<GridFormPopupProps, 'children'> {
  children: ReactNode;
  discussionTargetType: PatientTrackingDiscussionTargetType;
  discussionRecordId: string | null;
  currentUserId: string;
  currentUserName: string;
  currentUserAliases?: Iterable<string>;
  currentUserHasPhoto?: boolean | null;
  discussionRecordLabel?: string;
  patientTrackingApiBase?: string;
  /** When true, form is read-only and footer shows Close (+ Edit). Discussion stays visible. */
  viewMode?: boolean;
  onRequestEdit?: () => void;
}

const PLACEMENT_MENU_ITEMS: { id: RecordDiscussionPlacement; text: string }[] = [
  { id: 'left', text: 'Panel on left' },
  { id: 'right', text: 'Panel on right' },
  { id: 'off', text: 'Discussion off' }
];

interface DiscussionPanelProps {
  visible: boolean;
  discussionTargetType: PatientTrackingDiscussionTargetType;
  discussionRecordId: string;
  currentUserId: string;
  currentUserName: string;
  currentUserAliases?: Iterable<string>;
  currentUserHasPhoto?: boolean | null;
  recordLabel: string;
  patientTrackingApiBase?: string;
}

function RecordDiscussionPanel({
  visible,
  discussionTargetType,
  discussionRecordId,
  currentUserId,
  currentUserName,
  currentUserAliases,
  currentUserHasPhoto,
  recordLabel,
  patientTrackingApiBase
}: DiscussionPanelProps) {
  const { onPeerActivity } = useDiscussionMessageNotifications({
    enabled: visible,
    onOpenDiscussionPanel: () => undefined
  });

  const discussion = usePatientTrackingDiscussion(
    discussionTargetType,
    discussionRecordId,
    visible,
    {
      panelOpen: visible,
      recordLabel,
      currentUserId,
      currentUserAliases,
      onPeerActivity,
      apiBase: patientTrackingApiBase,
      onError: (message) => showToast(message, 'error')
    }
  );

  return (
    <PatientTrackingDiscussionChat
      messages={discussion.messages}
      targetType={discussionTargetType}
      targetId={discussionRecordId}
      currentUserId={currentUserId}
      currentUserAliases={currentUserAliases}
      currentUserName={currentUserName}
      currentUserHasPhoto={currentUserHasPhoto}
      loading={discussion.loading}
      onMessageEntered={discussion.sendMessage}
      onMessageUpdated={discussion.editMessage}
      onMessageDeleted={discussion.removeMessage}
      apiBase={patientTrackingApiBase}
    />
  );
}

/**
 * Two-pane record popup: form and per-record discussion, with user-configurable panel side.
 */
export function RecordEditDiscussionPopup({
  children,
  discussionTargetType,
  discussionRecordId,
  currentUserId,
  currentUserName,
  currentUserAliases,
  currentUserHasPhoto,
  discussionRecordLabel,
  patientTrackingApiBase,
  viewMode: viewModeProp = false,
  onRequestEdit,
  width = 1040,
  maxBodyHeight = 720,
  footer: editFooter,
  title,
  ...popupProps
}: RecordEditDiscussionPopupProps) {
  const placement = useRecordDiscussionPlacement();
  const autoHide = useRecordDiscussionAutoHide();
  const [editOverride, setEditOverride] = useState(false);
  const [discussionExpanded, setDiscussionExpanded] = useState(true);
  const [hoverOpen, setHoverOpen] = useState(false);

  useEffect(() => {
    if (!popupProps.visible) {
      setEditOverride(false);
      setHoverOpen(false);
      return;
    }
    if (viewModeProp) setEditOverride(false);
    const hideOnOpen =
      readAutoHideRecordDiscussionPanel() && placement !== 'off';
    setDiscussionExpanded(!hideOnOpen);
    setHoverOpen(false);
  }, [popupProps.visible, viewModeProp, placement]);

  const viewMode = editOverride ? false : viewModeProp;

  const showDiscussionColumn = placement !== 'off';
  const useAutoHide = autoHide && placement !== 'off';
  const effectiveDiscussionOpen = discussionExpanded || (useAutoHide && hoverOpen);
  const discussionCollapsed = showDiscussionColumn && !effectiveDiscussionOpen;

  const openHover = useCallback(() => {
    if (useAutoHide && !discussionExpanded) setHoverOpen(true);
  }, [discussionExpanded, useAutoHide]);

  const closeHover = useCallback(() => {
    if (useAutoHide && !discussionExpanded) setHoverOpen(false);
  }, [discussionExpanded, useAutoHide]);

  const onPlacementSelect = useCallback((value: RecordDiscussionPlacement) => {
    writeRecordDiscussionPlacement(value);
    if (value !== 'off') setDiscussionExpanded(true);
  }, []);

  const toolbarItems = useMemo((): ToolbarItem[] => {
    const items: ToolbarItem[] = [
      {
        widget: 'dxDropDownButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'preferences',
          hint: 'Discussion panel settings',
          stylingMode: 'text',
          showArrowIcon: false,
          dropDownOptions: { width: 200 },
          items: PLACEMENT_MENU_ITEMS,
          keyExpr: 'id',
          displayExpr: 'text',
          selectedItemKey: placement,
          onItemClick: (e: { itemData?: { id?: RecordDiscussionPlacement } }) => {
            const id = e.itemData?.id;
            if (id === 'left' || id === 'right' || id === 'off') onPlacementSelect(id);
          }
        }
      }
    ];

    if (showDiscussionColumn) {
      items.unshift({
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: discussionExpanded ? 'chevrondoubleleft' : 'chevrondoubleright',
          hint: discussionExpanded ? 'Collapse discussion panel' : 'Expand discussion panel',
          stylingMode: 'text',
          onClick: () => setDiscussionExpanded((v) => !v)
        }
      });
    }

    return items;
  }, [discussionExpanded, onPlacementSelect, placement, showDiscussionColumn]);

  const recordLabel =
    discussionRecordLabel ??
    (discussionTargetType === 'patient'
      ? 'patient record'
      : discussionTargetType === 'tracker'
        ? 'tracker issue'
        : 'covered entity');

  const discussionUnavailable = (
    <div className="pt-discussion-chat pt-discussion-chat--placeholder-only">
      <header className="pt-discussion-chat__header">
        <span className="pt-discussion-chat__header-title">Discussion</span>
      </header>
      <div className="pt-discussion-chat__placeholder">
        The discussion panel ran into a problem and was paused so you can keep editing this record.
        Close and reopen the record to try again.
      </div>
    </div>
  );

  const discussionPanel = discussionRecordId && popupProps.visible ? (
    <DiscussionErrorBoundary fallback={discussionUnavailable}>
      <RecordDiscussionPanel
        visible={popupProps.visible}
        discussionTargetType={discussionTargetType}
        discussionRecordId={discussionRecordId}
        currentUserId={currentUserId}
        currentUserAliases={currentUserAliases}
        currentUserName={currentUserName}
        currentUserHasPhoto={currentUserHasPhoto}
        recordLabel={recordLabel}
        patientTrackingApiBase={patientTrackingApiBase}
      />
    </DiscussionErrorBoundary>
  ) : discussionRecordId ? (
    <div className="pt-discussion-chat pt-discussion-chat--placeholder-only">
      <header className="pt-discussion-chat__header">
        <span className="pt-discussion-chat__header-title">Discussion</span>
      </header>
      <div className="pt-discussion-chat__placeholder">Loading discussion…</div>
    </div>
  ) : (
    <div className="pt-discussion-chat pt-discussion-chat--placeholder-only">
      <header className="pt-discussion-chat__header">
        <span className="pt-discussion-chat__header-title">Discussion</span>
      </header>
      <div className="pt-discussion-chat__placeholder">
        Save this record to start the discussion. You can @mention pharmacy ops teammates and covered entity alert
        contacts.
      </div>
    </div>
  );

  const footer = viewMode ? (
    <div className="patient-tracking-popup-footer">
      <div />
      <div className="patient-tracking-popup-footer__actions">
        <Button text="Close" onClick={popupProps.onClose} />
        {discussionRecordId ? (
          <Button
            text="Edit"
            type="default"
            onClick={() => {
              onRequestEdit?.();
              setEditOverride(true);
            }}
          />
        ) : null}
      </div>
    </div>
  ) : (
    editFooter
  );

  const layoutClassName = [
    'record-edit-discussion-popup',
    'ops-grid-form-popup--with-discussion',
    viewMode ? 'record-edit-discussion-popup--view' : '',
    `record-edit-discussion-popup--placement-${placement}`,
    discussionCollapsed ? 'record-edit-discussion-popup--discussion-collapsed' : '',
    useAutoHide ? 'record-edit-discussion-popup--auto-hide' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const hoverStrip =
    useAutoHide && discussionCollapsed ? (
      <div
        className={`record-edit-discussion-popup__hover-strip record-edit-discussion-popup__hover-strip--${placement}`}
        aria-hidden
        onMouseEnter={openHover}
      />
    ) : null;

  return (
    <GridFormPopup
      width={width}
      preferredHeight={840}
      minHeight={760}
      maxBodyHeight={maxBodyHeight}
      bodyClassName="ops-grid-detail-popup--with-discussion"
      toolbarItems={toolbarItems}
      {...popupProps}
      title={title}
      footer={footer}
    >
      <div className={layoutClassName}>
        {placement === 'left' ? hoverStrip : null}
        <div className="record-edit-discussion-popup__form">{children}</div>
        {showDiscussionColumn ? (
          <div
            className="record-edit-discussion-popup__discussion"
            onMouseEnter={useAutoHide ? openHover : undefined}
            onMouseLeave={useAutoHide ? closeHover : undefined}
          >
            {discussionPanel}
          </div>
        ) : null}
        {placement === 'right' ? hoverStrip : null}
      </div>
    </GridFormPopup>
  );
}
